/**
 * Contracts for module "content" (Content Tracker). Owner: partners.
 * The agency's posting and delivery tracker: clients (posting / delivery-only), team, platforms, content types and
 * stages, monthly scope, deliverables with per-platform posting status, delivery and client approval, rescheduling
 * and backlog, Daily Grid, dashboards, notifications, Excel import and export.
 * Status and count logic (BR-01..BR-13) runs only on the server (NFR-05); these schemas carry its results.
 */
import { z } from 'zod';

// ---------------------------------------------------------------- vocabulary
export const CLIENT_KINDS = ['posting', 'delivery'] as const;
export type ClientKind = (typeof CLIENT_KINDS)[number];

export const MEMBER_ROLES = ['designer', 'editor', 'freelancer', 'account_manager', 'social_media_manager'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

/** Effective tracker role of the caller. `admin` = agency owner/admin. */
export const TRACKER_ROLES = ['admin', ...MEMBER_ROLES] as const;
export type TrackerRole = (typeof TRACKER_ROLES)[number];

export const PLATFORM_STATUSES = ['planned', 'scheduled', 'posted', 'missed'] as const;
export type PlatformStatus = (typeof PLATFORM_STATUSES)[number];

export const APPROVALS = ['pending', 'approved', 'changes_requested'] as const;
export type Approval = (typeof APPROVALS)[number];

/** BR-04, in evaluation order (first match wins). */
export const POSTING_STATUSES = [
  'no_platform', 'posted', 'no_date', 'partial_overdue', 'overdue', 'at_risk', 'due_today', 'partially_posted', 'upcoming',
] as const;
export type PostingStatus = (typeof POSTING_STATUSES)[number];

/** BR-05, in evaluation order (first match wins). */
export const DELIVERY_STATUSES = ['revision_needed', 'approved', 'delivered', 'no_date', 'overdue', 'at_risk', 'due_today', 'upcoming'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export type ItemStatus = PostingStatus | DeliveryStatus;
export const ITEM_STATUSES = [...new Set<string>([...POSTING_STATUSES, ...DELIVERY_STATUSES])] as ItemStatus[];

/** BR-09 backlog schedule buckets. */
export const BACKLOG_BUCKETS = ['this_month', 'next_month', 'later', 'not_scheduled'] as const;
export type BacklogBucket = (typeof BACKLOG_BUCKETS)[number];

export const CATALOG_KINDS = ['platform', 'content_type', 'stage'] as const;
export type CatalogKind = (typeof CATALOG_KINDS)[number];

/** Defaults loaded on first use (US-03). Stages flagged early count toward At Risk (BR-06); ready ones are Ready backlog (BR-10). */
export const DEFAULT_CATALOG: Record<CatalogKind, readonly { key: string; label: string; early?: boolean; ready?: boolean }[]> = {
  platform: [
    { key: 'instagram', label: 'Instagram' }, { key: 'facebook', label: 'Facebook' }, { key: 'linkedin', label: 'LinkedIn' },
    { key: 'youtube', label: 'YouTube' }, { key: 'x', label: 'X' }, { key: 'tiktok', label: 'TikTok' },
  ],
  content_type: [{ key: 'reel', label: 'Reel' }, { key: 'static', label: 'Static' }],
  stage: [
    { key: 'concept', label: 'Concept', early: true },
    { key: 'copy_script', label: 'Copy / Script', early: true },
    { key: 'design_edit', label: 'Design / Edit', early: true },
    { key: 'internal_review', label: 'Internal Review' },
    { key: 'sent_to_client', label: 'Sent to Client', ready: true },
    { key: 'approved', label: 'Approved', ready: true },
    { key: 'scheduled', label: 'Scheduled', ready: true },
  ],
};

export const CONTENT_TIME_ZONE = 'Asia/Dubai';

// ---------------------------------------------------------------- primitives
export const LocalDateSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'YYYY-MM-DD');
export const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM');
const Key = z.string().trim().min(1).max(40).regex(/^[a-z0-9_]+$/, 'lowercase letters, digits and _');
const Id = z.string().min(1).max(64);
const HttpsUrl = z.string().trim().url().max(2000).refine((u) => /^https:\/\//i.test(u), 'https only');

// ---------------------------------------------------------------- settings and catalog
export const SettingsUpdateSchema = z.object({
  atRiskDays: z.number().int().min(0).max(30).optional(),
  digestHour: z.number().int().min(0).max(23).optional(),
}).strict();

export const CatalogCreateSchema = z.object({
  kind: z.enum(CATALOG_KINDS),
  key: Key,
  label: z.string().trim().min(1).max(60),
  sort: z.number().int().min(0).max(1000).optional(),
  isEarly: z.boolean().optional(),
  isReady: z.boolean().optional(),
}).strict();
export const CatalogUpdateSchema = z.object({
  label: z.string().trim().min(1).max(60).optional(),
  sort: z.number().int().min(0).max(1000).optional(),
  active: z.boolean().optional(),
  isEarly: z.boolean().optional(),
  isReady: z.boolean().optional(),
}).strict();

// ---------------------------------------------------------------- clients and team (US-01, US-02)
export const ClientCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  kind: z.enum(CLIENT_KINDS),
  accountManagerId: Id,
  /** posting clients default to every active platform when omitted */
  platforms: z.array(Key).max(20).optional(),
  /** optional link to an existing client workspace the agency manages */
  tenantId: Id.optional(),
}).strict();
export const ClientUpdateSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  accountManagerId: Id.optional(),
  platforms: z.array(Key).max(20).optional(),
  active: z.boolean().optional(),
}).strict();

export const MemberCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  role: z.enum(MEMBER_ROLES),
  email: z.string().trim().toLowerCase().email().max(254).optional(),
  /** the agency user this member signs in as; omit for people the AM updates on their behalf */
  userId: Id.optional(),
}).strict();
export const MemberUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  role: z.enum(MEMBER_ROLES).optional(),
  email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  userId: Id.nullable().optional(),
  active: z.boolean().optional(),
}).strict();

// ---------------------------------------------------------------- scope (US-04)
export const ScopePutSchema = z.object({
  clientId: Id,
  month: MonthSchema,
  items: z.array(z.object({ contentType: Key, quantity: z.number().int().min(0).max(10000) })).max(50),
}).strict();
export const ScopeCopySchema = z.object({
  /** copies this month's scope into the following month */
  fromMonth: MonthSchema,
  clientId: Id.optional(),
}).strict();

// ---------------------------------------------------------------- deliverables (US-05..US-23)
export const LinkSchema = z.object({ url: HttpsUrl, label: z.string().trim().max(120).optional() });

export const DeliverableCreateSchema = z.object({
  clientId: Id,
  contentType: Key,
  title: z.string().trim().min(1).max(200),
  originalDate: LocalDateSchema.optional(),
  assigneeId: Id.optional(),
  stage: Key.optional(),
  /** posting clients only; defaults to the client's enabled platforms */
  platforms: z.array(Key).max(20).optional(),
  links: z.array(LinkSchema).max(20).optional(),
  remarks: z.string().max(2000).optional(),
}).strict();

/** Optimistic locking (NFR-16): `version` must match the stored version. */
export const DeliverableUpdateSchema = z.object({
  version: z.number().int().min(1),
  title: z.string().trim().min(1).max(200).optional(),
  contentType: Key.optional(),
  assigneeId: Id.nullable().optional(),
  platforms: z.array(Key).max(20).optional(),
  links: z.array(LinkSchema).max(20).optional(),
  remarks: z.string().max(2000).nullable().optional(),
  actualPostDate: LocalDateSchema.nullable().optional(),
  /** only while the deliverable has no original date yet (BR-01: locked once set) */
  originalDate: LocalDateSchema.optional(),
}).strict();

export const StageSchema = z.object({ stage: Key.nullable(), version: z.number().int().min(1).optional() }).strict();

export const PlatformStatusSchema = z.object({
  status: z.enum(PLATFORM_STATUSES),
  postUrl: HttpsUrl.nullable().optional(),
}).strict();
export const BulkPostedSchema = z.object({
  platforms: z.array(Key).min(1).max(20),
  status: z.enum(PLATFORM_STATUSES).default('posted'),
  postUrls: z.record(z.string(), HttpsUrl).optional(),
}).strict();

export const RescheduleSchema = z.object({
  /** null clears the rescheduled date (back to the original date) */
  date: LocalDateSchema.nullable(),
  reason: z.string().trim().max(500).optional(),
}).strict();
export const OriginalDateSchema = z.object({ date: LocalDateSchema, reason: z.string().trim().min(3).max(500) }).strict();

export const DeliverySchema = z.object({
  deliveredOn: LocalDateSchema.optional(),
  fileLink: HttpsUrl.optional(),
}).strict();
export const ApprovalSchema = z.object({ approval: z.enum(APPROVALS), note: z.string().trim().max(1000).optional() }).strict();

export const FileVersionSchema = z.object({ url: HttpsUrl, label: z.string().trim().max(120).optional() }).strict();

export const GenerateSchema = z.object({
  clientId: Id,
  month: MonthSchema,
  /** spread the drafts across the month's days; otherwise they are undated */
  spread: z.boolean().default(false),
}).strict();

export const BacklogLoadSchema = z.object({
  clientId: Id,
  rows: z.array(z.object({ contentType: Key, month: MonthSchema, count: z.number().int().min(1).max(500) })).min(1).max(100),
}).strict();

export const DeliverableQuerySchema = z.object({
  clientId: Id.optional(),
  amId: Id.optional(),
  assigneeId: Id.optional(),
  contentType: Key.optional(),
  platform: Key.optional(),
  status: z.enum(ITEM_STATUSES as [ItemStatus, ...ItemStatus[]]).optional(),
  partial: z.enum(['true', 'false']).optional(),
  backlog: z.enum(['true', 'false']).optional(),
  bucket: z.enum(BACKLOG_BUCKETS).optional(),
  ready: z.enum(['true', 'false']).optional(),
  done: z.enum(['true', 'false']).optional(),
  kind: z.enum(CLIENT_KINDS).optional(),
  /** original-date month (BR-11) */
  month: MonthSchema.optional(),
  /** working-date range (Daily Grid / calendar click-through) */
  from: LocalDateSchema.optional(),
  to: LocalDateSchema.optional(),
  /** done-date month (backlog cleared click-through) */
  clearedIn: MonthSchema.optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(5000).default(1000),
});
export type DeliverableQuery = z.infer<typeof DeliverableQuerySchema>;

export const SavedFilterSchema = z.object({
  name: z.string().trim().min(1).max(80),
  query: z.record(z.string(), z.string().max(100)),
}).strict();

// ---------------------------------------------------------------- Excel import (US-32)
const Cell = z.union([z.string().max(2000), z.number(), z.null()]).optional();
const Row = z.record(z.string(), Cell);
export const ImportSchema = z.object({
  dryRun: z.boolean().default(true),
  setup: z.array(Row).max(500).default([]),
  postingLog: z.array(Row).max(20000).default([]),
  deliveryLog: z.array(Row).max(20000).default([]),
  monthlyScope: z.array(Row).max(5000).default([]),
}).strict();
export type ImportInput = z.infer<typeof ImportSchema>;

/**
 * Backlog CSV upload (US-21): one row per client, content type and original month with a count, parsed in the browser
 * like the Excel import. Client may be left out of the file: the rows then belong to `clientId` (the screen's client).
 * Dry run (default) reports errors and duplicates and writes nothing.
 */
export const BacklogImportSchema = z.object({
  dryRun: z.boolean().default(true),
  clientId: Id.optional(),
  rows: z.array(Row).min(1).max(1000),
}).strict();
export type BacklogImportInput = z.infer<typeof BacklogImportSchema>;
export interface BacklogImportReport {
  dryRun: boolean;
  counts: { rows: number; items: number; skippedDuplicates: number };
  errors: { row: number; message: string }[];
  duplicates: { row: number; key: string }[];
  committed: boolean;
}

// ---------------------------------------------------------------- views (server-computed)
export interface PlatformPostView { platform: string; status: PlatformStatus; postUrl: string | null; postedAt: string | null }
export interface DeliverableView {
  id: string; clientId: string; clientName: string; clientKind: ClientKind; accountManagerId: string;
  contentType: string; title: string; originalDate: string | null; planMonth: string | null; rescheduledDate: string | null; workingDate: string | null;
  stage: string | null; assigneeId: string | null; assigneeName: string | null; actualPostDate: string | null;
  links: { url: string; label?: string }[]; remarks: string | null;
  platforms: PlatformPostView[]; deliveredOn: string | null; deliveryLink: string | null; approval: Approval | null;
  status: ItemStatus; done: boolean; doneDate: string | null; partiallyPosted: boolean; atRisk: boolean;
  backlog: boolean; bucket: BacklogBucket | null; ready: boolean; version: number; updatedAt: string;
}

export interface TrackerMe {
  role: TrackerRole; memberId: string | null; partnerId: string; today: string; timeZone: string;
  can: { setup: boolean; scope: boolean; editDeliverables: boolean; stage: boolean; platformStatus: boolean; delivery: boolean; originalDate: boolean; allDashboards: boolean };
}
